import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Checkbox, TextField, IconButton, Select, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { FS } from "@plannel/utils/font-util";

/*
 * 측정치 설정 목록 — 차트 설정 모달의 표준 본문. (레퍼런스 화면: Sales Status Dashboard2)
 *
 * 측정치가 4개든 20+든 한 팝업으로 감당하도록:
 *   · 검색      : 항목 2개 초과면 항상 표시(입력 시 clear 버튼)
 *   · 전체선택  : 헤더 체크박스(indeterminate) — **보이는(필터된) 행만** 대상
 *   · 높이/스크롤:
 *       - 하한: 최대 4행분 고정 → 검색 매치가 1개여도 안 쪼그라듦
 *       - **10행까지는 스크롤 없음** — 모달 높이가 내용만큼 늘어난다
 *       - 10행을 넘으면 10행에서 잘라 스크롤 (뷰포트 초과분은 DialogContent 가 처리)
 *
 * ag-grid 를 쓰지 말 것 — 설정 목록은 이 컴포넌트가 표준이다.
 *
 * props
 *   measures   : [{ key, label, hasActive }]  — label 은 이미 번역된 문자열
 *   draft      : { [key]: { [typeField]: "BAR", activeFlg: true } }
 *   setDraft   : draft setState (함수형 업데이트 사용)
 *   chartTypes : 선택 가능한 차트 타입 값 배열. 예 ["BAR","LINE"] / ["LINE","BAR","AREA"]
 *                라벨은 t(값.toLowerCase()) 로 표시 → bar/line/area 키 필요(default ns)
 *   typeField  : draft 안의 차트타입 필드명. 기본 "chartType" (일부 레거시는 "type")
 *   open       : 모달 열림 — 열릴 때 검색어 초기화
 *   toolbarRight : 검색창 **오른쪽 같은 줄**에 놓을 컨트롤(예: DP 의 AMT/QTY 토글).
 *                  주면 검색창이 남는 폭만 차지해 짧아진다.
 */
// ROW_H 는 "대략" 이면 안 된다 — maxHeight 를 행 수로 환산하는 기준이라, 실제 행이 이보다 높으면
// 딱 임계값 행수에서 스크롤이 생긴다(py 0.5 + small Select 로 실제 44px 였고 10행에서 그랬다).
// 그래서 행 높이를 minHeight 로 이 값에 고정해 계산과 실제를 일치시킨다.
const ROW_H = 40;     // 행 1개 높이 — 행 sx 의 minHeight 와 반드시 같이 간다
const MIN_ROWS = 4;   // 높이 하한(행 수)
const MAX_ROWS = 10;  // 최대 표시 행 수. 이 수를 **넘을 때만** 잘라서 스크롤(10행은 안 잘림)

const MeasureConfigList = ({
  measures = [], draft, setDraft, chartTypes = ["BAR", "LINE"], typeField = "chartType", open, toolbarRight,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  useEffect(() => { if (open) setQuery(""); }, [open]);

  const setField = (key, field, v) => setDraft((d) => ({ ...d, [key]: { ...d[key], [field]: v } }));

  const anyActive = measures.some((r) => r.hasActive);
  const q = query.trim().toLowerCase();
  const rows = q ? measures.filter((r) => String(r.label ?? "").toLowerCase().includes(q)) : measures;
  const showSearch = measures.length > 1;   // 필터는 항상 표시. 스크롤은 maxHeight 임계값으로 자동

  // 전체선택 — 보이는 active 행 일괄 on/off
  const activeRows = rows.filter((r) => r.hasActive);
  const allChecked = activeRows.length > 0 && activeRows.every((r) => draft?.[r.key]?.activeFlg !== false);
  const someChecked = activeRows.some((r) => draft?.[r.key]?.activeFlg !== false);
  const toggleAll = () => setDraft((d) => {
    const next = { ...d };
    activeRows.forEach((r) => { next[r.key] = { ...next[r.key], activeFlg: !allChecked }; });
    return next;
  });

  return (
    <>
      {/* 검색 + (선택) 우측 컨트롤을 한 줄에 — toolbarRight 가 있으면 검색창은 남는 폭만 차지 */}
      {(showSearch || toolbarRight) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {showSearch && (
            <TextField size="small" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search")}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 18, color: "text.disabled", mr: 0.5 }} />,
                endAdornment: query ? (
                  <IconButton size="small" onClick={() => setQuery("")} aria-label="clear search" sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </IconButton>
                ) : null,
              }}
              sx={{ flex: 1, minWidth: 0, "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }} />
          )}
          {/* 우측 컨트롤 — 레거시 토글이 자체 float/margin 을 갖는 경우가 있어 여기서 중앙 정렬을 보정 */}
          {toolbarRight && (
            <Box sx={{
              display: "flex", alignItems: "center", flexShrink: 0,
              "& > *": { float: "none !important", mt: "0 !important", mb: "0 !important" },
            }}>
              {toolbarRight}
            </Box>
          )}
        </Box>
      )}
      {anyActive && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox size="small" checked={allChecked} indeterminate={someChecked && !allChecked}
              onChange={toggleAll} sx={{ p: 0.25 }} />
            <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("activeFlg")}</Typography>
          </Box>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("chartType")}</Typography>
        </Box>
      )}
      {/* 하한(최대 4행분)만 고정 → 검색 매치가 적어도 과하게 안 줄어듦.
          MAX_ROWS 이하면 maxHeight 없이 모달이 늘어나고, 넘을 때만 MAX_ROWS 에서 잘라 스크롤. */}
      <Box sx={{
        minHeight: Math.min(measures.length, MIN_ROWS) * ROW_H,
        ...(measures.length > MAX_ROWS
          ? { maxHeight: MAX_ROWS * ROW_H, overflowY: "auto" }
          : {}),
      }}>
        {rows.map((r) => (
          // 여백은 py 가 아니라 minHeight 로 — 행 높이를 ROW_H 에 맞춰야 위 maxHeight 환산이 맞는다
          <Box key={r.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: ROW_H, gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              {r.hasActive && (
                <Checkbox size="small" checked={draft?.[r.key]?.activeFlg !== false}
                  onChange={(e) => setField(r.key, "activeFlg", e.target.checked)} sx={{ p: 0.25 }} />
              )}
              {/* 측정치 색 스와치 — color 를 준 경우에만(차트가 실제로 쓰는 색). 미지정 모달은 영향 없음 */}
              {r.color && (
                <Box sx={{
                  width: 11, height: 11, borderRadius: "2px", flexShrink: 0, mr: 0.25,
                  bgcolor: r.color, border: "1px solid", borderColor: "divider",
                }} />
              )}
              <Typography sx={{ fontSize: FS.TITLE, color: "text.primary" }}>{r.label}</Typography>
            </Box>
            <Select size="small" value={draft?.[r.key]?.[typeField] ?? chartTypes[0]}
              onChange={(e) => setField(r.key, typeField, e.target.value)} sx={{ minWidth: 90, fontSize: FS.TITLE }}>
              {chartTypes.map((ct) => (
                <MenuItem key={ct} value={ct} sx={{ fontSize: FS.TITLE }}>{t(String(ct).toLowerCase())}</MenuItem>
              ))}
            </Select>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default MeasureConfigList;
